import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  OnInit,
  OnDestroy,
  signal,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  Rule,
  getPasswordRules,
  getConfirmPasswordRules,
} from '../../../../utils/password.rules';
import { InputComponent, IconComponent, CheckboxComponent, RecaptchaComponent } from '../../../../../@ads/components';
import { FormRulesBoxComponent } from '../../../../components/form-rules-box/form-rules-box.component';
import { CreateAccountPjFormHandler } from '../../create-account-pj-form.handler';

@Component({
  selector: 'app-pj-password-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    IconComponent,
    CheckboxComponent,
    RecaptchaComponent,
    FormRulesBoxComponent,
  ],
  templateUrl: './pj-password-form.component.html',
  styleUrl: './pj-password-form.component.scss',
})
export class PjPasswordFormComponent implements OnInit, OnDestroy {
  private readonly _handler = inject(CreateAccountPjFormHandler);
  private readonly _destroy$ = new Subject<void>();

  @Input() hideRecaptcha = false;

  @Output() setStepValidation = new EventEmitter<boolean>();
  @Output() changeRecaptcha = new EventEmitter<string>();

  public readonly passwordRules: Rule[] = getPasswordRules();
  public readonly confirmPasswordRules: Rule[] = getConfirmPasswordRules();

  public showPassword = signal(false);
  public showConfirmPassword = signal(false);
  public passwordsMatch = signal(false);
  public focusedField = signal<'password' | 'confirmPassword' | null>(null);
  public rules: Rule[] = [];

  private _passwordRules: Rule[] = this.passwordRules;
  private _confirmPasswordRules: Rule[] = this.confirmPasswordRules;

  public form = new FormGroup(
    {
      password: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      confirmPassword: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      termos: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
      recaptchaReactive: new FormControl('', { nonNullable: true }),
    },
    { validators: this.checkPasswords.bind(this) }
  );

  get isPasswordValid(): boolean {
    return this._passwordRules.every((r) => r.valid);
  }

  ngOnInit(): void {
    if (!this.hideRecaptcha) {
      this.form.controls.recaptchaReactive.setValidators([Validators.required]);
      this.form.controls.recaptchaReactive.updateValueAndValidity();
    }

    const saved = this._handler.getPassword();
    if (saved) {
      this.form.patchValue({ password: saved, confirmPassword: saved, termos: this._handler.getTermsAccepted() });
    }

    this.form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((val) => {
      this.setStepValidation.emit(this.form.valid);
      this._handler.setTermsAccepted(this.form.controls.termos.value === true);
      if (this.form.valid) {
        this._handler.setPassword(this.form.controls.password.value);
      }
      if (val.recaptchaReactive) {
        this.changeRecaptcha.emit(val.recaptchaReactive);
      }
    });

    this.setStepValidation.emit(this.form.valid);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  togglePassword(isConfirm = false): void {
    if (isConfirm) {
      this.showConfirmPassword.update((v) => !v);
    } else {
      this.showPassword.update((v) => !v);
    }
  }

  showPasswordRulesTooltip(field: 'password' | 'confirmPassword', _event: FocusEvent): void {
    this.focusedField.set(field);
    if (field === 'confirmPassword') {
      this.rules = this._confirmPasswordRules;
      this.validateConfirmPasswordRules();
    } else {
      this.rules = this._passwordRules;
      this.validatePasswordRules();
    }
  }

  clearFocusedField(): void {
    setTimeout(() => {
      const active = document.activeElement;
      const isField =
        active?.closest?.('[data-pj-password-field="password"]') ||
        active?.closest?.('[data-pj-password-field="confirmPassword"]');
      if (!isField) this.focusedField.set(null);
    }, 200);
  }

  validatePasswordRules(): void {
    const password = this.form.controls.password.value;
    this.form.controls.password.setErrors(null);
    this._passwordRules.forEach((rule) => {
      rule.valid = new RegExp(rule.regex, 'g').test(password);
      if (!rule.valid) {
        this.form.controls.password.setErrors({ [rule.key]: true });
      }
    });
    const confirm = this.form.controls.confirmPassword.value;
    if (confirm) this.validateConfirmPasswordRules();
  }

  validateConfirmPasswordRules(): void {
    const password = this.form.controls.password.value;
    const confirmPassword = this.form.controls.confirmPassword.value;
    this.form.controls.confirmPassword.setErrors(null);
    this._confirmPasswordRules.forEach((rule) => {
      rule.valid = !!(password && confirmPassword && password === confirmPassword);
      if (!rule.valid) {
        this.form.controls.confirmPassword.setErrors({ [rule.key]: true });
      }
    });
  }

  private checkPasswords(control: AbstractControl): { [key: string]: any } | null {
    this.validatePassword(control.get('password')?.value ?? '');
    const pass = control.get('password')?.value;
    const confirmPass = control.get('confirmPassword')?.value;
    this.passwordsMatch.set(!!(pass && confirmPass && pass === confirmPass));
    if (this.isPasswordValid && this.passwordsMatch()) return null;
    return { notSame: true };
  }

  private validatePassword(password: string): void {
    this._passwordRules.forEach((rule) => {
      rule.valid = new RegExp(rule.regex, 'g').test(password);
    });
  }
}
